/**
 * PRD-1544: turns a failed file's `retry` object (from GET /datasets/{id}/files)
 * and the per-file rejections in the retry 202 response into readable copy, and
 * decides whether a Retry control should be offered.
 *
 * Contract (PRD-1543):
 *   retry = { eligible, action: 'retry'|'replace', reason_code, failed_stage,
 *             attempts_used, attempts_max }
 *   reason codes — retryable: transient_error, triage_failed, platform_error
 *                  replace:   unreadable_document, quality_gate_failed, no_stored_copy,
 *                             upload_rejected (PRD-1610: refused at upload, nothing stored)
 *                  rejection: already_processing, retry_limit_reached, not_found
 */

// Why a file failed — shown next to it. Kept plain and non-technical; `failed_stage`
// is for display elsewhere, not folded in here.
const REASON_COPY = {
  transient_error: 'A temporary error interrupted processing.',
  triage_failed: 'We couldn’t sort this file for processing.',
  platform_error: 'A system error interrupted processing.',
  unreadable_document: 'This file couldn’t be read.',
  quality_gate_failed: 'This file didn’t pass quality checks.',
  no_stored_copy: 'No stored copy is available to retry.',
  upload_rejected: 'Rejected at upload.',
};

// Per-file rejections returned in the retry 202 `rejected[]` — shown inline on the
// file, never as a generic page error.
const REJECTION_COPY = {
  already_processing: 'Already processing.',
  retry_limit_reached: 'Retry limit reached — replace this file.',
  not_found: 'This file is no longer available.',
  unreadable_document: 'This file couldn’t be read — replace it.',
  quality_gate_failed: 'This file didn’t pass quality checks — replace it.',
  no_stored_copy: 'No stored copy is available — replace this file.',
};

/** The `retry` the backend gives a file rejected at upload (PRD-1610), for when
 *  it leaves `retry` off: a rejected file can only be replaced. */
export const UPLOAD_REJECTED_RETRY = Object.freeze({ eligible: false, action: 'replace', reason_code: 'upload_rejected' });

/** attempts_max - attempts_used, never negative. 0 for a missing retry object. */
export function attemptsRemaining(retry) {
  if (!retry) return 0;
  const used = Number(retry.attempts_used) || 0;
  const max = Number(retry.attempts_max) || 0;
  return Math.max(0, max - used);
}

/** Offer a Retry control only when the backend says eligible + retry AND an
 *  attempt actually remains (defense in depth against a stale eligible flag). */
export function canRetryFile(retry) {
  return Boolean(retry && retry.eligible && retry.action === 'retry' && attemptsRemaining(retry) > 0);
}

function reasonText(reasonCode) {
  return REASON_COPY[reasonCode] || 'Processing failed.';
}

/** One line of status for a failed file: the reason, plus either attempts-left
 *  (retryable) or a replace prompt (replace / limit reached). */
export function retryStatusText(retry) {
  if (!retry) return '';
  const base = reasonText(retry.reason_code);
  const remaining = attemptsRemaining(retry);
  // Limit reached (marked retry but nothing left) reads as a replace, not a retry.
  if (retry.action === 'retry' && remaining > 0) {
    return `${base} ${remaining} of ${retry.attempts_max} attempts left.`;
  }
  if (retry.action === 'retry' && remaining === 0) {
    return `${base} Retry limit reached — replace this file by re-uploading it.`;
  }
  // Nothing was stored, so it is a good copy that's wanted, not the same file again.
  if (retry.reason_code === 'upload_rejected') return `${base} Upload a valid copy.`;
  // action === 'replace'
  return `${base} Replace this file by re-uploading it.`;
}

/** Readable copy for a per-file rejection reason_code from the retry 202. */
export function rejectionText(reasonCode) {
  return REJECTION_COPY[reasonCode] || 'This file couldn’t be retried.';
}
